// React lifecycle for the Intercom refund conversation: phase state + one AbortController
// per session that disposes every wait/observer. Thin adapter over intercomMessenger +
// refundSessionStore. See ../README.md.
import { useCallback, useEffect, useRef, useState } from 'react'
import Intercom, {
  boot as bootIntercom,
  update as updateIntercom,
  showNewMessage as showNewIntercomMessage,
  showConversation as showIntercomConversation,
  showSpace as showIntercomSpace,
  show as showIntercom,
  hide as hideIntercom,
  hideNotifications as hideIntercomNotifications,
  onHide as onHideIntercom,
  shutdown as shutdownIntercom
} from '@intercom/messenger-js-sdk'

import {
  RefundMethodValue,
  RefundSessionMarker,
  readMarker,
  readMarkerMap,
  writeMarker,
  clearMarker
} from './refundSessionStore'
import {
  INTERCOM_Z_BEHIND,
  INTERCOM_Z_FRONT,
  clearIntercomClientState,
  isOnConversationRoute,
  isPanelOnRoute,
  isPanelOnSpace,
  injectHostStyle,
  removeHostStyle,
  sizePanel,
  suppressOpenAnimation,
  lockDownChrome,
  captureConversationId,
  readKnownConversationIds,
  pollUntil
} from './intercomMessenger'

export type { RefundMethodValue, RefundSessionMarker }

interface UseIntercomRefundSessionArgs {
  appId?: string
  userId?: string
  email?: string
  name?: string
  order: { guid: string; rxGuid: string } | null
  // Pre-signed for warm() only, which always pre-boots as Toast Cash ahead of the guest's choice.
  // open() takes its jwt/attrs from the caller — the card is the one place that knows every method.
  toastCashJwt?: string
  toastCashAttributes: Record<string, unknown> | null
}

const REFUND_ACTIVE_BODY_CLASS = 'tds-refund-support-active'
// Kept visibility:hidden by SCSS while present — the loading buffer over the open window.
const REFUND_OPENING_BODY_CLASS = 'tds-refund-opening'
// 80ms × 75 = 6s fail-open ceiling. See ../README.md § Reveal buffer.
const SETTLE_POLL_MS = 80
const SETTLE_MAX_ATTEMPTS = 75
// Backstop only — the auto-collapse we swallow is identified by the one-shot flag below, not by
// elapsed time. This just stops a never-arriving auto-collapse from leaving the flag armed forever;
// a wider window would only widen the gap in which a guest's first close is swallowed.
const AUTO_COLLAPSE_GRACE_MS = 1500

const swallowAbort = (e: unknown) => {
  if (!(e instanceof DOMException && e.name === 'AbortError')) throw e
}

const bodyClass = (cls: string, on: boolean) =>
  document.body.classList.toggle(cls, on)

export function useIntercomRefundSession(args: UseIntercomRefundSessionArgs) {
  const {
    appId,
    userId,
    email,
    name,
    order,
    toastCashJwt,
    toastCashAttributes
  } = args

  const [phase, setPhase] = useState<'idle' | 'warming' | 'opening' | 'open'>(
    'idle'
  )
  const phaseRef = useRef(phase)
  phaseRef.current = phase

  // One controller per active session; abort() disposes every wait + observer it owns.
  const sessionCtl = useRef<AbortController | null>(null)
  // Guest's explicit close (× / backdrop) vs. Intercom's post-submit auto-collapse.
  const explicitlyClosing = useRef(false)
  const lastSubmitAt = useRef(0)
  // Armed by a successful submit, consumed by the very next hide — that one is Intercom's
  // auto-collapse. Every hide after it is the guest closing the panel.
  const pendingAutoCollapse = useRef(false)

  const beginSession = useCallback(() => {
    sessionCtl.current?.abort()
    const { signal } = (sessionCtl.current = new AbortController())
    pendingAutoCollapse.current = false
    // clearActive() arms this and tears down without hideIntercom(), so no onHide clears it — a
    // stale `true` makes the NEXT session read the post-submit auto-collapse as a guest close.
    explicitlyClosing.current = false
    injectHostStyle()
    bodyClass(REFUND_ACTIVE_BODY_CLASS, true)
    bodyClass(REFUND_OPENING_BODY_CLASS, true)
    suppressOpenAnimation(signal)
    return signal
  }, [])

  const poll = useCallback(
    (predicate: () => boolean, signal: AbortSignal) =>
      pollUntil(predicate, {
        signal,
        intervalMs: SETTLE_POLL_MS,
        maxAttempts: SETTLE_MAX_ATTEMPTS
      }),
    []
  )

  const setZ = (z: number) => {
    try {
      updateIntercom({ z_index: z })
    } catch {
      /* SDK not ready */
    }
  }

  const teardownSurface = useCallback(() => {
    bodyClass(REFUND_ACTIVE_BODY_CLASS, false)
    bodyClass(REFUND_OPENING_BODY_CLASS, false)
    removeHostStyle()
    setZ(INTERCOM_Z_BEHIND)
  }, [])

  // Abort the session and drop the surface. Every exit from a started session must reach this:
  // beginSession() applies the visibility:hidden buffer, so bailing strands an invisible panel.
  const endSession = useCallback(() => {
    sessionCtl.current?.abort()
    teardownSurface()
    setPhase('idle')
  }, [teardownSurface])

  // Terminal handler for a session's async block. swallowAbort() alone won't do: it RETHROWS a real
  // failure, leaving the phase at 'opening' behind the hidden buffer. Abort must NOT tear down — it
  // is the normal supersede path, so a newer session already owns sessionCtl and the surface.
  const failSession = useCallback(
    (e: unknown) => {
      if (e instanceof DOMException && e.name === 'AbortError') return
      endSession()
    },
    [endSession]
  )

  const reveal = useCallback((signal: AbortSignal) => {
    sizePanel(signal)
    lockDownChrome(signal)
    setZ(INTERCOM_Z_FRONT)
    bodyClass(REFUND_OPENING_BODY_CLASS, false)
    setPhase('open')
  }, [])

  const bootBehind = useCallback(
    (jwt: string | undefined, attrs: Record<string, unknown> | null) => {
      if (!appId) return
      bootIntercom({
        app_id: appId,
        region: 'us',
        hide_default_launcher: true,
        hide_notifications: true,
        z_index: INTERCOM_Z_BEHIND,
        user_id: userId,
        ...(email ? { email } : {}),
        ...(name ? { name } : {}),
        intercom_user_jwt: jwt,
        ...(attrs ?? {})
      })
      hideIntercomNotifications(true)
    },
    [appId, userId, email, name]
  )

  const onHide = useCallback(() => {
    // Intercom auto-collapses exactly once, right after a submit — keep the panel up for that hide
    // only. Consume the flag either way, so a guest's own close is honoured on the next hide.
    const autoCollapse =
      pendingAutoCollapse.current &&
      Date.now() - lastSubmitAt.current < AUTO_COLLAPSE_GRACE_MS
    pendingAutoCollapse.current = false
    if (
      !explicitlyClosing.current &&
      phaseRef.current === 'open' &&
      autoCollapse
    ) {
      try {
        showIntercom()
      } catch {
        /* noop */
      }
      return
    }
    explicitlyClosing.current = false
    teardownSurface()
    setPhase('idle')
  }, [teardownSurface])

  // `known` is snapshotted by the caller BEFORE clearIntercomClientState() wipes the intercom.*
  // open-state — otherwise a re-synced conversation could be mistaken for this tab's submit.
  const trackSubmit = useCallback(
    (marker: RefundSessionMarker, signal: AbortSignal, known: Set<string>) => {
      if (marker.sent && marker.conversationId) return
      captureConversationId(known, signal)
        .then((id) => {
          lastSubmitAt.current = Date.now()
          pendingAutoCollapse.current = true
          writeMarker({ ...marker, conversationId: id, sent: true })
        })
        .catch(swallowAbort)
    },
    []
  )

  // Intercom's own persisted ids + every id in our marker map. Read before clearing that state.
  const knownConversationIds = useCallback(
    () =>
      new Set<string>([
        ...readKnownConversationIds(appId),
        ...Object.values(readMarkerMap())
          .map((m) => m.conversationId)
          .filter((id): id is string => !!id)
      ]),
    [appId]
  )

  const warm = useCallback(() => {
    if (!appId || !userId || !toastCashJwt) return
    beginSession()
    try {
      bootBehind(toastCashJwt, toastCashAttributes)
      hideIntercom()
      setPhase('warming')
    } catch {
      // SDK not ready — open() will boot instead. Drop the surface beginSession() applied.
      endSession()
    }
  }, [
    appId,
    userId,
    toastCashJwt,
    toastCashAttributes,
    beginSession,
    bootBehind,
    endSession
  ])

  // showNewMessage (not startConversation, which sends a blank message and dead-ends the workflow)
  // must run only after the connect/restore lands, so it is the last writer. `jwt`/`attrs` come from
  // the caller: only the card knows which of its four methods' signatures to use.
  const open = useCallback(
    (
      method: RefundMethodValue,
      jwt: string | undefined,
      attrs: Record<string, unknown> | null
    ) => {
      if (!appId || !userId || !order) return
      const signal = beginSession()
      setPhase('opening')

      const marker: RefundSessionMarker = {
        orderGuid: order.guid,
        rxGuid: order.rxGuid,
        method,
        sent: false,
        userId
      }

      // Snapshot known ids BEFORE clearing state (see trackSubmit).
      const known = knownConversationIds()
      // Both throw if the SDK script hasn't landed, and they run outside the async block, so an
      // unguarded throw would leave the phase stuck at 'opening' behind the hidden buffer. The
      // marker is written only once the boot succeeds, so a failed open leaves no phantom refund.
      try {
        bootBehind(jwt, attrs)
        onHideIntercom(onHide)
        writeMarker(marker)
      } catch {
        endSession()
        return
      }
      ;(async () => {
        clearIntercomClientState()
        showIntercom()
        await poll(() => isOnConversationRoute(appId), signal)
        showNewIntercomMessage('')
        trackSubmit(marker, signal, known)
        await poll(() => isPanelOnRoute(appId), signal)
        reveal(signal)
      })().catch(failSession)
    },
    [
      appId,
      userId,
      order,
      beginSession,
      bootBehind,
      onHide,
      poll,
      trackSubmit,
      knownConversationIds,
      reveal,
      endSession,
      failSession
    ]
  )

  // Never re-boots — that would open a new conversation and duplicate the refund.
  const resume = useCallback(
    (marker: RefundSessionMarker) => {
      if (!marker.conversationId || !appId) return
      const { conversationId } = marker
      const signal = beginSession()
      setPhase('opening')
      // Same hazard as open(): synchronous, throws if the SDK never landed, and it runs after the
      // hidden buffer went on — an unguarded throw leaves an invisible, undismissable panel.
      try {
        onHideIntercom(onHide)
      } catch {
        endSession()
        return
      }
      ;(async () => {
        showIntercom()
        await poll(() => isOnConversationRoute(appId), signal)
        showIntercomConversation(conversationId)
        await poll(() => isPanelOnRoute(appId, conversationId), signal)
        reveal(signal)
      })().catch(failSession)
    },
    [appId, beginSession, onHide, poll, reveal, endSession, failSession]
  )

  // Route to one of the Messenger's own spaces — the guest's ticket list, for the already-reported
  // exception. NEVER boots: it is reached from an already-identified session, and re-booting would
  // re-sign the attributes and open a SECOND conversation. See ../README.md § Exception states.
  const openSpace = useCallback(
    (spaceName: string) => {
      if (!appId) return
      const signal = beginSession()
      setPhase('opening')
      // Same hazard as resume().
      try {
        onHideIntercom(onHide)
      } catch {
        endSession()
        return
      }
      ;(async () => {
        showIntercom()
        // showSpace has to be the LAST writer: Intercom's own restore navigates the router when
        // the panel comes up, and would land the guest on that instead. Same rule as resume().
        await poll(isPanelOnSpace, signal)
        showIntercomSpace(spaceName)
        reveal(signal)
      })().catch(failSession)
    },
    [appId, beginSession, onHide, poll, reveal, endSession, failSession]
  )

  // Open a NEW support conversation carrying an exception's signed attributes. Boots, unlike
  // openSpace, because 'Selected refund method' is "require verified updates": a JWT boot is the
  // only way to set it, update() claims being silently dropped (../README.md § attribute contract).
  // Writes no marker and tracks no submit — a support contact, not a refund reservation, so there is
  // nothing to resume into a refund later and nothing for the refund workflow to file.
  const openSupport = useCallback(
    (jwt: string | undefined, attrs: Record<string, unknown> | null) => {
      if (!appId || !userId || !jwt) return
      const signal = beginSession()
      setPhase('opening')
      try {
        bootBehind(jwt, attrs)
        onHideIntercom(onHide)
      } catch {
        endSession()
        return
      }
      ;(async () => {
        clearIntercomClientState()
        showIntercom()
        await poll(() => isOnConversationRoute(appId), signal)
        showNewIntercomMessage('')
        await poll(() => isPanelOnRoute(appId), signal)
        reveal(signal)
      })().catch(failSession)
    },
    [
      appId,
      userId,
      beginSession,
      bootBehind,
      onHide,
      poll,
      reveal,
      endSession,
      failSession
    ]
  )

  const forgetOrder = useCallback(clearMarker, [])

  // Load the SDK once (anonymous, behind the page); clear open-state so reloads start closed.
  useEffect(() => {
    if (!appId) return
    clearIntercomClientState()
    Intercom({
      app_id: appId,
      region: 'us',
      hide_default_launcher: true,
      hide_notifications: true,
      z_index: INTERCOM_Z_BEHIND
    })
  }, [appId])

  // On unmount, dispose the session AND shut the Messenger down, so its iframe/timers and onHide
  // callbacks don't survive navigation away from the confirmation page.
  useEffect(
    () => () => {
      sessionCtl.current?.abort()
      teardownSurface()
      try {
        shutdownIntercom()
      } catch {
        /* SDK not booted */
      }
    },
    [teardownSurface]
  )

  const clearActive = useCallback(() => {
    explicitlyClosing.current = true
    endSession()
  }, [endSession])

  const hasLiveSession = useCallback(
    () => typeof window.Intercom === 'function',
    []
  )

  return {
    phase,
    warm,
    open,
    resume,
    openSpace,
    openSupport,
    forgetOrder,
    readMarker,
    clearActive,
    hasLiveSession
  }
}
